/**
 * Inventory WebSocket Service
 *
 * Manages real-time inventory updates, stock alerts, and notifications
 */

import { StockAlert } from '@/types/inventory';

import WebSocketService from './websocket.service';

// ============================================================================
// Types
// ============================================================================

export interface InventoryUpdate {
  id: string;
  type:
    | 'stock_update'
    | 'low_stock_alert'
    | 'out_of_stock_alert'
    | 'reorder_alert';
  itemId: string;
  itemName: string;
  sku: string;
  currentStock: number;
  previousStock?: number;
  reorderLevel?: number;
  timestamp: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
}

export interface StockMovement {
  id: string;
  itemId: string;
  itemName: string;
  sku: string;
  movementType: 'in' | 'out' | 'adjustment' | 'reserved' | 'released';
  quantity: number;
  previousStock: number;
  newStock: number;
  reason?: string;
  userId?: string;
  timestamp: string;
}

export interface RealtimeMetrics {
  totalItems: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  averageTurnover: number;
  lastUpdated: string;
}

// ============================================================================
// Inventory WebSocket Service Class
// ============================================================================

export class InventoryWebSocketService extends WebSocketService {
  private isMonitoring = false;
  private monitoredItems: Set<string> = new Set();

  constructor() {
    super({
      url: import.meta.env.DEV
        ? 'ws://localhost:3001/ws/inventory'
        : 'wss://api.veggieworld.africa/ws/inventory',
      heartbeatInterval: 15000,
      maxRetries: 5,
      retryDelay: 2000,
    });
  }

  // ============================================================================
  // Subscription Methods
  // ============================================================================

  /**
   * Subscribe to real-time inventory updates
   */
  subscribeToInventoryUpdates(
    handler: (update: InventoryUpdate) => void
  ): () => void {
    return this.on('inventory_update', message => {
      handler(message.payload as InventoryUpdate);
    });
  }

  /**
   * Subscribe to stock movement events
   */
  subscribeToStockMovements(
    handler: (movement: StockMovement) => void
  ): () => void {
    return this.on('stock_movement', message => {
      handler(message.payload as StockMovement);
    });
  }

  /**
   * Subscribe to stock alerts (low stock, out of stock, etc.)
   */
  subscribeToStockAlerts(handler: (alert: StockAlert) => void): () => void {
    return this.on('stock_alert', message => {
      handler(message.payload as StockAlert);
    });
  }

  /**
   * Subscribe to real-time inventory metrics
   */
  subscribeToRealtimeMetrics(
    handler: (metrics: RealtimeMetrics) => void
  ): () => void {
    return this.on('realtime_metrics', message => {
      handler(message.payload as RealtimeMetrics);
    });
  }

  /**
   * Subscribe to bulk operations progress
   */
  subscribeToBulkOperations(handler: (progress: any) => void): () => void {
    return this.on('bulk_operation_progress', message => {
      handler(message.payload);
    });
  }

  // ============================================================================
  // Control Methods
  // ============================================================================

  /**
   * Start monitoring inventory for real-time updates
   */
  startInventoryMonitoring(itemIds?: string[]): boolean {
    if (itemIds) {
      itemIds.forEach(id => this.monitoredItems.add(id));
    }

    const success = this.send({
      type: 'start_inventory_monitoring',
      payload: {
        itemIds: itemIds || Array.from(this.monitoredItems),
        includeMetrics: true,
        includeAlerts: true,
        includeMovements: true,
      },
    });

    if (success) {
      this.isMonitoring = true;
    }

    return success;
  }

  /**
   * Stop inventory monitoring
   */
  stopInventoryMonitoring(): boolean {
    const success = this.send({
      type: 'stop_inventory_monitoring',
      payload: {},
    });

    if (success) {
      this.isMonitoring = false;
      this.monitoredItems.clear();
    }

    return success;
  }

  /**
   * Monitor specific inventory items
   */
  monitorItems(itemIds: string[]): boolean {
    itemIds.forEach(id => this.monitoredItems.add(id));

    return this.send({
      type: 'monitor_items',
      payload: {
        itemIds,
      },
    });
  }

  /**
   * Stop monitoring specific items
   */
  stopMonitoringItems(itemIds: string[]): boolean {
    itemIds.forEach(id => this.monitoredItems.delete(id));

    return this.send({
      type: 'stop_monitoring_items',
      payload: {
        itemIds,
      },
    });
  }

  /**
   * Subscribe to low stock alerts for specific items
   */
  subscribeToLowStockAlerts(itemIds: string[], threshold?: number): boolean {
    return this.send({
      type: 'subscribe_low_stock_alerts',
      payload: {
        itemIds,
        threshold,
      },
    });
  }

  /**
   * Set alert thresholds for specific items
   */
  setAlertThresholds(
    thresholds: { itemId: string; lowStock: number; critical: number }[]
  ): boolean {
    return this.send({
      type: 'set_alert_thresholds',
      payload: {
        thresholds,
      },
    });
  }

  /**
   * Request immediate inventory status update
   */
  requestInventoryStatus(itemIds?: string[]): boolean {
    return this.send({
      type: 'request_inventory_status',
      payload: {
        itemIds,
      },
    });
  }

  /**
   * Acknowledge an alert (mark as seen/handled)
   */
  acknowledgeAlert(alertId: string, userId: string): boolean {
    return this.send({
      type: 'acknowledge_alert',
      payload: {
        alertId,
        userId,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Report stock discrepancy for investigation
   */
  reportDiscrepancy(
    itemId: string,
    expectedStock: number,
    actualStock: number,
    reason: string
  ): boolean {
    return this.send({
      type: 'report_discrepancy',
      payload: {
        itemId,
        expectedStock,
        actualStock,
        reason,
        timestamp: new Date().toISOString(),
      },
    });
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get monitoring status
   */
  getMonitoringStatus() {
    return {
      isMonitoring: this.isMonitoring,
      monitoredItemsCount: this.monitoredItems.size,
      monitoredItems: Array.from(this.monitoredItems),
      connectionStatus: this.getStatus(),
    };
  }

  /**
   * Join a room for category-based updates
   */
  joinCategoryRoom(category: string): boolean {
    return this.send({
      type: 'join_category_room',
      payload: {
        category,
      },
    });
  }

  /**
   * Leave a category room
   */
  leaveCategoryRoom(category: string): boolean {
    return this.send({
      type: 'leave_category_room',
      payload: {
        category,
      },
    });
  }

  /**
   * Setup batch monitoring for multiple categories
   */
  setupBatchMonitoring(categories: string[], alertTypes: string[]): boolean {
    return this.send({
      type: 'setup_batch_monitoring',
      payload: {
        categories,
        alertTypes,
      },
    });
  }

  // ============================================================================
  // Override connection handling for inventory-specific logic
  // ============================================================================

  connect(): void {
    super.connect();

    // Re-establish monitoring on reconnection
    this.onStatusChange(status => {
      if (status === 'connected' && this.monitoredItems.size > 0) {
        // Re-establish monitoring after reconnection
        setTimeout(() => {
          this.startInventoryMonitoring();
        }, 1000);
      }
    });
  }
}

// ============================================================================
// Export singleton instance
// ============================================================================

export const inventoryWebSocket = new InventoryWebSocketService();
export default InventoryWebSocketService;
